import { ResourceClient } from '../../../api/resource';
import { companiesApi, lookupApis, pocApis } from '../../admin/admin.api';

export interface MasterFieldDef {
  /** Request key, snake_case, matching the controller's body. */
  name: string;
  label: string;
  required?: boolean;
  type?: 'text' | 'textarea' | 'number' | 'boolean';
  helperText?: string;
  defaultValue?: unknown;
}

export interface MasterConfig {
  /** Singular noun for the dialog title and toasts, e.g. "Locality". */
  entityName: string;
  client: ResourceClient<any>;
  fields: MasterFieldDef[];
  /** Where the option label lives on an API row — lookups use `label`, contacts use `name`. */
  labelKey: 'label' | 'name';
}

/**
 * The masters the job edit screen can add to and edit in place.
 *
 * Each form carries the same fields as that master's own admin screen — the four contact
 * types match their Contact Masters tab, the lookups match their Reference Data tab — so
 * a row created mid-job-edit is as complete as one created from the master screen, and
 * nothing has to be filled in twice.
 *
 * Status is absent on purpose. Status rows carry a category and drive workflow logic — the
 * API special-cases the "Canceled" label to also store a cancellation reason — so a status
 * invented here would be accepted by the form and then ignored by the code behind it.
 */

/** mobile/phone/whatsapp/email — the contact block all four POC tables share. */
const contactFields: MasterFieldDef[] = [
  { name: 'mobile', label: 'Mobile' },
  { name: 'phone', label: 'Phone' },
  { name: 'whatsapp', label: 'WhatsApp' },
  { name: 'email', label: 'Email' },
];

const nameField: MasterFieldDef = { name: 'name', label: 'Name', required: true };

/**
 * code/label/description/sort/active — the shape the four job lookups share. `code` is
 * optional: left blank, the controller derives it from the label.
 */
const lookupFields = (labelText: string): MasterFieldDef[] => [
  { name: 'label', label: labelText, required: true },
  { name: 'code', label: 'Code', helperText: 'Leave blank to derive it from the name' },
  { name: 'description', label: 'Description', type: 'textarea' },
  { name: 'sort_order', label: 'Sort Order', type: 'number' },
  { name: 'is_active', label: 'Active', type: 'boolean', defaultValue: true },
];

export const JOB_MASTERS = {
  /**
   * The firm's own companies (V19). Same fields as the Company Master screen. Writing
   * needs ADMIN on the API, so screens offer "+ Add" / edit for it to admins only.
   */
  companies: {
    entityName: 'Company',
    client: companiesApi,
    labelKey: 'name',
    fields: [
      nameField,
      { name: 'address', label: 'Address', type: 'textarea' },
      { name: 'contact_no1', label: 'Contact No. 1' },
      { name: 'contact_no2', label: 'Contact No. 2' },
      { name: 'email', label: 'Email' },
      { name: 'website', label: 'Website' },
      { name: 'gst_no', label: 'GST No.', helperText: '15 characters, e.g. 27ABCDE1234F1Z5' },
      { name: 'registration_no', label: 'Registration No.' },
      { name: 'city', label: 'City' },
      { name: 'state', label: 'State' },
      { name: 'country', label: 'Country', defaultValue: 'India' },
      { name: 'is_active', label: 'Active', type: 'boolean', defaultValue: true },
    ],
  },
  localities: {
    entityName: 'Locality',
    client: lookupApis.localities,
    labelKey: 'label',
    fields: lookupFields('Locality Name'),
  },
  projectTypes: {
    entityName: 'Project Type',
    client: lookupApis.projectTypes,
    labelKey: 'label',
    fields: lookupFields('Project Type'),
  },
  constructionWorkTypes: {
    entityName: 'Construction Work Type',
    client: lookupApis.constructionWorkTypes,
    labelKey: 'label',
    fields: lookupFields('Construction Work Type'),
  },
  buildingTypes: {
    entityName: 'Building Type',
    client: lookupApis.buildingTypes,
    labelKey: 'label',
    fields: lookupFields('Building Type'),
  },
  clients: {
    entityName: 'Client',
    client: pocApis.clients,
    labelKey: 'name',
    fields: [
      nameField,
      { name: 'company_name', label: 'Company Name' },
      ...contactFields,
      { name: 'office_address', label: 'Office Address', type: 'textarea' },
      { name: 'gst_number', label: 'GST Number' },
      { name: 'work_order', label: 'Work Order' },
    ],
  },
  architects: {
    entityName: 'Architect',
    client: pocApis.architects,
    labelKey: 'name',
    fields: [nameField, ...contactFields, { name: 'address', label: 'Address', type: 'textarea' }],
  },
  builders: {
    entityName: 'Builder',
    client: pocApis.builders,
    labelKey: 'name',
    fields: [nameField, ...contactFields, { name: 'address', label: 'Address', type: 'textarea' }],
  },
  owners: {
    entityName: 'Owner',
    client: pocApis.owners,
    labelKey: 'name',
    fields: [
      nameField,
      { name: 'building_name', label: 'Building Name' },
      ...contactFields,
      { name: 'office_address', label: 'Office Address', type: 'textarea' },
    ],
  },
} as const satisfies Record<string, MasterConfig>;

export type JobMasterKey = keyof typeof JOB_MASTERS;
